import React from 'react';
import * as yup from 'yup';
import { useRouter } from 'next/navigation';
import { FormattedMessage, useIntl } from 'react-intl';
import { SubmitHandler, useForm } from 'react-hook-form';
import { CLOUDFRONT_ASSET_URL } from '@osmosis/constants';
import { useColors } from '@osmosis/react-ui/hooks/useColors';
import { Box } from '@osmosis/react-ui/components/Box';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { faDownload } from '@fortawesome/pro-solid-svg-icons/faDownload';
import themeSchema from '../../../lib/composer/schemas/theme';
import textSchema from '../../../lib/composer/schemas/text';
import subheadingSchema from '../../../lib/composer/schemas/subheading';
import headingSchema from '../../../lib/composer/schemas/heading';
import BlockWrapper from '../BlockWrapper';
import Heading from '../block-ui/Heading';
import Subheading from '../block-ui/Subheading';
import RichText from '../block-ui/RichText';
import { useFetch } from '../../../context/FetchProvider';
import { useUserContext } from '../../../context/UserContext';
import useModal from '../../../hooks/useModal';
import { ConfirmModal } from '../../ConfirmModal';
import EditablePropsType, {
  ComposerBlockElement,
  BookDownloadBlockProps,
  Book,
} from '../../../types/composer';
import { yupResolver } from '@hookform/resolvers/yup';
import { authMessages } from '../../../constants/authLocalization';
import InputValidation from '../../InputValidation';

const EMAIL = 'email';
type FormValues = { email: string };

/**
 * Book download block.
 */
const BookDownload: ComposerBlockElement = (props: BookDownloadBlockProps) => {
  const {
    heading,
    subheading,
    text,
    theme,
    books,
    dialogMessage,
    campaign,
    requiresAccount,
    nodeID,
    topHeader,
  } = props;

  const [selectedBook, setSelectedBook] = React.useState<Book>();
  const { user } = useUserContext();
  const {
    hide: hideConfirmationModal,
    isVisible: isConfirmationModalVisible,
    setIsVisible: setConfirmationModalIsVisible,
  } = useModal();
  const { post } = useFetch();
  const intl = useIntl();
  const router = useRouter();

  const schema = yup
    .object({
      [EMAIL]: yup
        .string()
        .email(intl.formatMessage(authMessages.invalidEmail))
        .required(intl.formatMessage(authMessages.emailRequired)),
    })
    .required();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
  });
  const errorMessage = errors?.[EMAIL]?.message;

  const colors = useColors();
  const bg = (theme && colors[theme]) || 'bg-0';

  const downloadBook = (book: Book) => {
    if (user) {
      window.open(book.url, '_blank');
      return;
    }

    if (requiresAccount) {
      router.push('/login');
      return;
    }

    setConfirmationModalIsVisible(true);
    setSelectedBook(book);
  };

  const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
    // save email only for live pages
    if (nodeID) {
      await post('/postEmailForm', {
        campaign,
        email: data.email,
        pageID: nodeID,
      });
    }

    hideConfirmationModal();
    window.open(selectedBook?.url, '_blank');
  };

  return (
    <Box bg={bg}>
      <BlockWrapper>
        <Flex className="flex-col md:flex-row">
          <Flex className="flex-col mr-[25px] flex-[40%_1_0] gap-5">
            <Heading text={heading || ''} theme={theme} topHeader={topHeader} />

            {subheading && (
              <Subheading
                text={subheading}
                theme={theme}
                topHeader={topHeader}
              />
            )}
            {text && <RichText theme={theme} html={text} />}
          </Flex>

          <Flex className="flex-[60%_1_0] justify-center flex-wrap">
            {books?.map((book, idx) => (
              <Flex
                key={idx}
                className="flex-col items-center gap-5 p-5 w-[50%]"
              >
                <Subheading
                  text={book.name}
                  theme={theme}
                  topHeader={topHeader}
                />

                <img src={book.image} alt={book.name} />
                <Button
                  rounded
                  size="l"
                  startIcon={<Icon icon={faDownload} />}
                  onClick={() => downloadBook(book)}
                  data-testid={`${book.name}-download`}
                >
                  <FormattedMessage
                    id="common.download"
                    defaultMessage="Download"
                  />
                </Button>
              </Flex>
            ))}
          </Flex>
        </Flex>
      </BlockWrapper>

      <ConfirmModal
        hide={hideConfirmationModal}
        isVisible={isConfirmationModalVisible}
        headerContent="Enter your email"
        handleConfirm={handleSubmit(onSubmit)}
        confirmText={
          <FormattedMessage id="common.download" defaultMessage="Download" />
        }
      >
        <Flex className="p-4 max-w-[600px]">
          <form className="w-[100%]">
            <Flex className="mb-4 gap-5 justify-around">
              <Flex className="flex-col w-[60%] gap-5">
                <P
                  dangerouslySetInnerHTML={{ __html: `${dialogMessage}` }}
                  className="text-pretty"
                />

                <InputValidation
                  inputProps={{
                    ...register(EMAIL),
                    placeholder: 'Email',
                    'data-testid': 'download-book-email-input',
                  }}
                  error={!!errorMessage}
                  errorMessage={errorMessage as string}
                />
              </Flex>
              <img
                className="w-[100px] h-[100px]"
                src={`${CLOUDFRONT_ASSET_URL}taWCMycfR-6-JMA0zuwWBe_tR4iqSKad.png`}
                // eslint-disable-next-line formatjs/no-literal-string-in-jsx
                alt="Lungs"
              />
            </Flex>
          </form>
        </Flex>
      </ConfirmModal>
    </Box>
  );
};

BookDownload.schema = {
  name: 'bookDownload',
  label: 'Book Download',
  editableProps: [
    headingSchema,
    subheadingSchema,
    textSchema,
    {
      label: 'RequiresAccount',
      name: 'requiresAccount',
      type: EditablePropsType.Boolean,
    },
    {
      label: 'DialogMessage',
      name: 'dialogMessage',
      type: EditablePropsType.RichText,
    },
    {
      label: 'Books',
      name: 'books',
      type: EditablePropsType.Array,
      items: {
        label: 'Book',
        name: 'book',
        type: EditablePropsType.Object,
        properties: {
          name: {
            label: 'Name',
            name: 'name',
            type: EditablePropsType.Text,
          },
          image: {
            label: 'Image',
            name: 'image',
            type: EditablePropsType.Media,
            accept: ['image/*'],
          },
          url: {
            label: 'Url',
            name: 'url',
            type: EditablePropsType.Media,
            accept: ['application/pdf'],
          },
        },
      },
    },
    {
      label: 'Campaign',
      name: 'campaign',
      type: EditablePropsType.Object,
      properties: {
        name: {
          label: 'Name',
          name: 'name',
          type: EditablePropsType.Text,
        },
        type: {
          label: 'Type',
          name: 'type',
          type: EditablePropsType.Select,
          selectOptions: {
            options: [
              { label: '', value: '' },
              { label: 'b2b', value: 'b2b' },
              { label: 'b2c', value: 'b2c' },
            ],
          },
        },
      },
    },
    themeSchema,
  ],
};

export default BookDownload;
